import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const NEXUS360_URL = process.env.REACT_APP_NEXUS360_URL || 'http://localhost:3000';

// Mapping entre routes et modules requis
const ROUTE_MODULE_MAP = {
  '/dashboard': 'dashboard',
  '/employees': 'employees',
  '/habilitations': 'habilitations',
  '/absences': 'conges',
  '/calendar': 'calendar',
  '/payroll': 'payroll',
  '/documents': 'documents',
  '/templates': 'documents',
  '/interviews': 'entretiens',
  '/expenses': 'expenses',
  '/rse': 'rse',
  '/ai-chat': 'ai_chat',
  '/admin': 'admin',
  '/planning': 'planning',
  '/emargement': 'emargement',
  '/formations': 'formations',
  '/sous-traitants': 'sous_traitants',
  '/reporting': 'reporting',
  '/audit-log': 'admin',
  '/settings': null,
  '/subscription': null,
  '/portal': null,
  '/superadmin': null,
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [allowedModules, setAllowedModules] = useState([]);
  const [allModulesAccess, setAllModulesAccess] = useState(false);
  const [permissions, setPermissions] = useState([]);

  // Resolve token: URL param (fresh SSO redirect) wins over localStorage.
  // Earlier impl preferred localStorage but that locked stale tokens in
  // place after a re-login from nexus-360 — fixed.
  const resolveToken = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('token');
    if (urlToken) {
      localStorage.setItem('token', urlToken);
      // Use window.location.replace (not replaceState) so React Router
      // re-mounts on the target path. replaceState only updates the URL
      // bar — the rendered route stays where it was, leaving the user
      // stranded on the Landing page after an SSO redirect.
      const targetPath = (window.location.pathname === '/' || window.location.pathname === '')
        ? '/dashboard'
        : window.location.pathname + window.location.hash;
      window.location.replace(targetPath);
      return urlToken;
    }
    const t = localStorage.getItem('token');
    if (t) return t;
    return null;
  }, []);

  const fetchModules = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/structure/modules`);
      setAllowedModules(response.data.modules || []);
      setAllModulesAccess(response.data.all_access || false);
    } catch (error) {
      console.error('Error fetching modules:', error);
      setAllowedModules([]);
      setAllModulesAccess(false);
    }
  }, []);

  const redirectToNexus360Login = () => {
    const redirect = encodeURIComponent(window.location.origin + window.location.pathname);
    window.location.href = `${NEXUS360_URL}/login?redirect=${redirect}`;
  };

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      setSubscriptionStatus(response.data.subscription_status);
      setPermissions(response.data.permissions || []);
      await fetchModules();
    } catch (error) {
      console.error('Error fetching user:', error);
      localStorage.removeItem('token');
      redirectToNexus360Login();
    } finally {
      setLoading(false);
    }
  }, [fetchModules]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = resolveToken();
    if (t) {
      setToken(t);
      axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
      fetchUser();
    } else {
      setLoading(false);
      // No token — redirect to Nexus-360 login UNLESS on public landing page
      const publicPaths = ['/', '/login', '/register'];
      if (!publicPaths.includes(window.location.pathname)) {
        redirectToNexus360Login();
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // SSO: login redirects to Nexus-360
  const login = async () => {
    redirectToNexus360Login();
    return { success: false, error: 'Redirection vers Nexus-360...' };
  };

  // SSO: register redirects to Nexus-360
  const register = async () => {
    const redirect = encodeURIComponent(window.location.origin);
    window.location.href = `${NEXUS360_URL}/register?redirect=${redirect}`;
    return { success: false, error: 'Redirection vers Nexus-360...' };
  };

  // SSO: logout clears local state + redirects to Nexus-360
  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setSubscriptionStatus(null);
    setAllowedModules([]);
    setAllModulesAccess(false);
    setPermissions([]);
    window.location.href = `${NEXUS360_URL}/login`;
  };

  const refreshSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/check-subscription`);
      setSubscriptionStatus(response.data);
      await fetchModules();
      return response.data;
    } catch (error) {
      console.error('Error checking subscription:', error);
      return null;
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      setSubscriptionStatus(response.data.subscription_status);
      setPermissions(response.data.permissions || []);
      await fetchModules();
      return response.data;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };

  const needsSubscription = () => {
    if (!subscriptionStatus) return false;
    if (subscriptionStatus.status === 'super_admin') return false;
    if (subscriptionStatus.status === 'subscribed') return false;
    if (subscriptionStatus.status === 'trial' && subscriptionStatus.valid) return false;
    if (subscriptionStatus.status === 'payment_failed') return true;
    return subscriptionStatus.status === 'trial_expired' || !subscriptionStatus.valid;
  };

  const getTrialInfo = () => {
    if (!subscriptionStatus) return null;
    if (subscriptionStatus.status !== 'trial') return null;
    return {
      daysRemaining: subscriptionStatus.days_remaining,
      endDate: subscriptionStatus.trial_end_date
    };
  };

  const hasModuleAccess = (moduleCode) => {
    if (user?.role === 'super_admin') return true;
    if (allModulesAccess) return true;
    return allowedModules.includes(moduleCode);
  };

  const hasRouteAccess = (path) => {
    const moduleCode = ROUTE_MODULE_MAP[path];
    if (moduleCode === null || moduleCode === undefined) return true;
    return hasModuleAccess(moduleCode);
  };

  const getModuleForRoute = (path) => {
    return ROUTE_MODULE_MAP[path] || null;
  };

  const hasPermission = (permission) => {
    if (user?.role === 'super_admin') return true;
    return permissions.includes(permission);
  };

  // Navigate back to Nexus-360 dashboard
  const goToNexus360 = () => {
    window.location.href = NEXUS360_URL;
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading,
      login, register, logout,
      subscriptionStatus, refreshSubscriptionStatus, refreshUser,
      needsSubscription, getTrialInfo,
      allowedModules, allModulesAccess, hasModuleAccess, hasRouteAccess, getModuleForRoute, fetchModules,
      permissions, hasPermission,
      goToNexus360, NEXUS360_URL
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
